import { t as localeText } from "@/shared/lib/translate";
import { useTranslation as useLocaleSubscription } from "react-i18next";
import type { ComponentPropsWithoutRef, MouseEventHandler, ReactNode, Ref } from "react";
import { cn } from "@/shared/lib/cn";
import { allowCreditAction } from "@/shared/lib/creditAccess";
import { Button, type ButtonProps } from "./Button";
import { CreditAmount } from "./CreditAmount";
import { Surface } from "./Surface";
import { ArrowUpIcon } from "./icons";
import "./CreationStudio.css";
import { useCreationWorkspaceLayout } from "./CreationWorkspaceLayout";

/**
 * One grid template for every media composer. The controls column and the
 * creations column keep the exact same width in image, video, and audio so
 * switching workflow never shifts the layout.
 */
const composerGridClass =
  "items-stretch min-[900px]:grid-cols-[minmax(300px,326px)_minmax(0,1fr)] min-[1180px]:grid-cols-[326px_minmax(0,1fr)]";

export interface CreationComposerProps {
  children: ReactNode;
  className?: string;
}

/**
 * Shared two-pane shell for every media composer. Media-specific controls stay
 * in the first slot and the shared results surface stays in the second.
 */
export function CreationComposer({
  children,
  className,
}: CreationComposerProps) {
  const workspace = useCreationWorkspaceLayout();
  if (workspace) return <div className="studio-creation-options min-w-0">{children}</div>;
  return (
    <div
      className={cn(
        "studio-composer grid min-h-[var(--workspace-content-height,calc(100dvh-56px))]",
        composerGridClass,
        className,
      )}
    >
      {children}
    </div>
  );
}

export interface CreationComposerControlPanelProps
  extends ComponentPropsWithoutRef<"form"> {
  /**
   * Below the two-pane breakpoint the panel can present as a bottom sheet.
   * The caller owns the open state and the focus handling around it.
   */
  compact?: boolean;
  open?: boolean;
  onRequestClose?: () => void;
  closeLabel?: string;
  sheetRef?: Ref<HTMLDivElement>;
  sheetLabelledBy?: string;
  sheetHeader?: ReactNode;
}

/**
 * The one control-panel surface. Every composer renders its media controls
 * inside this component, so the padding, radius, sticky behaviour, and scroll
 * container are identical across workflows.
 */
export function CreationComposerControlPanel({
  children,
  className,
  compact = false,
  open = false,
  onRequestClose,
  closeLabel,
  sheetRef,
  sheetLabelledBy,
  sheetHeader,
  onSubmit,
  ...props
}: CreationComposerControlPanelProps) {
  useLocaleSubscription();
  const workspace = useCreationWorkspaceLayout();
  const handleSubmit: NonNullable<CreationComposerControlPanelProps["onSubmit"]> = (event) => {
    const submitter = (event.nativeEvent as SubmitEvent).submitter;
    const creditAction = submitter
      ? submitter.getAttribute("data-credit-action") === "true"
      : Boolean(event.currentTarget.querySelector('[data-credit-action="true"]'));
    if (creditAction && !allowCreditAction()) {
      event.preventDefault();
      return;
    }
    onSubmit?.(event);
  };
  if (workspace) return <form className={cn("studio-creation-form min-w-0", className)} {...props} onSubmit={handleSubmit}>{children}</form>;
  const panel = (
    <Surface
      as="form"
      tone="glass"
      border="none"
      radius={compact ? "lg" : "none"}
      data-compact={compact || undefined}
      className={cn(
        "studio-composer-panel min-w-0 p-4 sm:p-5",
        compact
          ? "max-h-[92dvh] w-full overflow-y-auto rounded-b-none [box-shadow:var(--shadow-soft)]"
          : "min-[900px]:sticky min-[900px]:top-[var(--composer-inset,12px)] min-[900px]:max-h-[var(--composer-panel-height,var(--workspace-content-height,calc(100dvh-56px)))] min-[900px]:overflow-y-auto",
        className,
      )}
      {...props}
      onSubmit={handleSubmit}
    >
      {compact && sheetHeader ? sheetHeader : null}
      {children}
    </Surface>
  );

  if (!compact) return panel;
  if (!open) return null;

  return (
    <div
      ref={sheetRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={sheetLabelledBy}
      className="fixed inset-0 z-50 flex items-end justify-center bg-[rgb(0_0_0_/_0.48)] p-0"
    >
      <button
        type="button"
        aria-label={closeLabel ?? localeText("interface.close_bbfa773e", { defaultValue: "Close" })}
        className="absolute inset-0 cursor-default"
        onClick={onRequestClose}
      />
      <div className="relative w-full">{panel}</div>
    </div>
  );
}

export type CreationComposerSubmitButtonProps = Omit<
  ButtonProps,
  "children"
> & {
  label: ReactNode;
  credits: ReactNode;
  creditLabel: ReactNode;
  /** A round action for the docked prompt bar, with its cost beside it. */
  compact?: boolean;
};

/** Exact credits share the action's color and appear only while it is available. */
export function CreationComposerSubmitButton({
  label,
  credits,
  creditLabel,
  className,
  disabled,
  loading,
  compact = false,
  onClick,
  ...props
}: CreationComposerSubmitButtonProps) {
  const requiresCredits = credits !== 0;
  const handleClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    if (requiresCredits && !allowCreditAction()) {
      event.preventDefault();
      return;
    }
    onClick?.(event);
  };
  const showCredits = credits !== null && credits !== undefined && !disabled && !loading && props["aria-disabled"] !== true && props["aria-disabled"] !== "true";
  if (compact) return (
    <span className="studio-prompt-submit">
      {showCredits ? <CreditAmount amount={credits} creditLabel={creditLabel} className="studio-prompt-cost" /> : null}
      <Button type="submit" variant="accent" disabled={disabled} loading={loading}
        className={cn("studio-composer-submit studio-prompt-go", className)} {...props}
        data-credit-action={requiresCredits} onClick={handleClick}>
        {!loading ? <ArrowUpIcon aria-hidden="true" /> : null}<span className="sr-only">{label}</span>
      </Button>
    </span>
  );
  return (
    <Button
      type="submit"
      variant="accent"
      size="lg"
      disabled={disabled}
      loading={loading}
      className={cn("studio-composer-submit w-full", className)}
      {...props}
      data-credit-action={requiresCredits}
      onClick={handleClick}
    >
      <span className={cn("grid w-full items-center gap-2", showCredits ? "grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]" : "grid-cols-1")}>
        {showCredits ? <span aria-hidden="true" /> : null}
        <span>{label}</span>
        {showCredits ? <CreditAmount amount={credits} creditLabel={creditLabel} className="studio-composer-cost justify-self-end text-[12px]" /> : null}
      </span>
    </Button>
  );
}
